import React, { useEffect, useState } from "react";
import { differenceWith, intersection, union, isEqual } from "lodash";

import DropDown from "./dropdown/DropDown";
import { FILTER_SELECTION_TYPE } from "./dropdown/DropDown.d";

import ZFIcon from "../../ui-component/ZFIcon/ZFIcon";
import {
	TFilterDropDownConfig,
	TFilterDropDownData,
} from "../../../fixtures/data-fixtures/ComponentConfigFixtures";

interface ICommonDropdownProps {
	configs: TFilterDropDownConfig[];
	basedOnSelection: TFilterDropDownData[];
	rest: TFilterDropDownData[];
	selected: TFilterDropDownData[];
	onFilterChange: (filterItems: TFilterDropDownData[]) => void;
}

interface IDropdownInput {
	config: TFilterDropDownConfig;
	basedOnSelection: TFilterDropDownData[];
	rest: TFilterDropDownData[];
	selected: TFilterDropDownData[];
}

const CommonDropdown = (props: ICommonDropdownProps) => {
	const { configs, basedOnSelection, rest, selected, onFilterChange } = props;
	const [dropDownInput, setDropDownInput] = useState([]);

	useEffect(() => {
		const updatedDropDownInput = configs.map((config) => {
			return {
				config: config,
				basedOnSelection: basedOnSelection
					? basedOnSelection.filter((_) => _.dimension === config.key)
					: [],
				rest: rest
					? rest.filter((_) => _.dimension === config.key)
					: [],
				selected: selected
					? selected.filter((_) => _.dimension === config.key)
					: [],
			} as IDropdownInput;
		});

		setDropDownInput(updatedDropDownInput);
	}, [configs, basedOnSelection, rest, selected]);

	/**
	 *
	 * @param filterItems : Filters selected from each dropdown
	 * @param dimension : like module_id, buyer
	 *
	 * 1. Check if change in filter has happened or not.
	 * 2. if there is a change then replace the filter for the dimension
	 * 3. combined all the filter
	 * 4. send to parent for state change
	 *
	 * to Handle if basedOnSelection the append
	 */

	const onDropDownFilterChange = (
		filterItems: TFilterDropDownData[],
		dimension: string,
		filterSelectionType?: FILTER_SELECTION_TYPE
	) => {
		console.log(
			"onDropDownFilterChange",
			filterItems,
			dimension,
			filterSelectionType
		);

		if (selected.length === 0 && filterItems.length > 0) {
			onFilterChange(filterItems);
			return;
		}

		const prevSelectFilterItemForDimension = dropDownInput.find(
			(_) => _.config.key === dimension
		).selected;

		const newFilterFoundInPrevious = intersection(
			prevSelectFilterItemForDimension,
			filterItems
		);

		const process1 = () => {
			if (
				newFilterFoundInPrevious.length !== filterItems.length ||
				(filterItems.length === 0 &&
					prevSelectFilterItemForDimension.length !== 0)
			) {
				dropDownInput.find(
					(_) => _.config.key === dimension
				).selected = filterItems;

				const reducer = (
					acc: TFilterDropDownData[],
					curr: IDropdownInput
				) => {
					return union(acc, curr.selected);
				};
				onFilterChange(dropDownInput.reduce(reducer, []));
			}
		};

		switch (filterSelectionType) {
			case FILTER_SELECTION_TYPE.BASED_ON_SELECTION: {
				process1();
				return;
			}

			case FILTER_SELECTION_TYPE.REST: {
				dropDownInput.forEach((d) => (d.selected = []));
				onFilterChange(filterItems);
				return;
			}

			case FILTER_SELECTION_TYPE.BOTH:
				dropDownInput.forEach((d) => (d.selected = []));
				onFilterChange(filterItems);
				return;
			default:
				break;
		}
	};

	const onFilterRemove = (item: TFilterDropDownData) => {
		onFilterChange(differenceWith(selected, [item], isEqual));
	};

	const onClearAllSelectedFilter = () => {
		onFilterChange([]);
	};

	return (
		<div className="common-dropdown-wrapper">
			<div className="align-item">
				{dropDownInput.map((item, index) => {
					return (
						<DropDown
							key={`dd_${item.config.id}_${item.config.key}_${index}`}
							config={item.config}
							basedOnSelection={item.basedOnSelection}
							rest={item.rest}
							currentSelection={item.selected}
							onDropDownFilterChange={onDropDownFilterChange}
						/>
					);
				})}
			</div>
			<div className="align-item">
				{selected.map((filter, index) => {
					return (
						<ShowFilters
							key={`sf_${filter.value}_${index}`}
							filter={filter}
							index={index}
							onFilterRemove={onFilterRemove}
						/>
					);
				})}
			</div>
			{selected.length > 0 && (
				<div
					onClick={() => onClearAllSelectedFilter()}
					className="zf-filter-btn zf-filter-btn__clear zf-text-black-80 zf-text-s zf-margin-r-xxl-s zf-margin-t-xxl-m zf-padding-t-xs zf-padding-r-s zf-padding-b-xs zf-padding-l-s"
				>
					Clear All
				</div>
			)}
		</div>
	);
};

export default CommonDropdown;

interface IFilterView {
	filter: TFilterDropDownData;
	index: number;
	onFilterRemove: (filter: TFilterDropDownData) => void;
}

const ShowFilters = (props: IFilterView) => {
	const { filter, index, onFilterRemove } = props;
	return (
		<div
			key={`sf_${filter.value}_${index}`}
			className="align-item zf-margin-r-xxl-s zf-margin-t-xxl-m zf-padding-t-xs zf-padding-r-s zf-padding-b-xs zf-padding-l-s zf-text-black-80 zf-text-s zf-filter-btn zf-filter-btn__selected"
		>
			<div>{filter.value}</div>
			<div
				className="zf-margin-l-xxl-8"
				onClick={() => onFilterRemove(filter)}
			>
				<ZFIcon
					iconName="cross"
					iconClasses={["zf-icon-react--size-s"]}
				/>
			</div>
		</div>
	);
};
